/**
 * A saved result in a cell the template seeds with its DEFAULT (reopen, 2026-10-20).
 *
 * Colilert-18 Presence/Absence (8103) puts `FIELD("DEFAULTRESULT")` in its Result cell J11 — the merge
 * seeds every run with the parameter's default ("Absent") — while DataParsing maps that same cell to
 * RESULT (Read + Write), so what the analyst picks there is saved as the run's Result. OG reopens its
 * saved sheet, so the pick ("Broken in transit") comes back; G6 re-merges, filled the cell from its FIELD
 * name, and every saved pick turned back into "Absent" on reopen (user report 2026-10-20).
 *
 * So, before the merge: for each field DataParsing places in a cell whose template formula is exactly
 * `FIELD("DEFAULTRESULT")`, a record that HAS a saved value for that field hands it to DEFAULTRESULT. A
 * record with nothing saved keeps the default, exactly what a fresh batch shows. Only the DEFAULTRESULT
 * seed is redirected — a cell bound to any other field keeps its own value (that field IS the table's).
 */
type Row = Record<string, unknown>;
type Ws = import('exceljs').Worksheet;

const DEFAULT_FIELD = 'DEFAULTRESULT';
const PURE_DEFAULT_RE = /^\s*=?\s*FIELD\(\s*"DEFAULTRESULT"\s*\)\s*$/i;

function formulaOf(ws: Ws, a1: string): string | null {
  try {
    const v = ws.getCell(a1).value as { formula?: string; sharedFormula?: string } | null;
    if (v && typeof v === 'object') return v.formula ?? null;
  } catch { /* an unparseable address — not a cell this rule applies to */ }
  return null;
}

/** The DataParsing fields whose cell on the template's main sheet is a pure FIELD("DEFAULTRESULT"). */
export function fieldsSeededByDefault(ws: Ws, fieldCells: Record<string, string>): string[] {
  const out: string[] = [];
  for (const [field, a1] of Object.entries(fieldCells)) {
    if (!field || field.toUpperCase() === DEFAULT_FIELD || !a1) continue;
    const f = formulaOf(ws, a1.replace(/\$/g, '').trim());
    if (f && PURE_DEFAULT_RE.test(f)) out.push(field);
  }
  return out;
}

function keyOf(rec: Row, field: string): string | undefined {
  const u = field.toUpperCase();
  return Object.keys(rec).find(k => k.toUpperCase() === u);
}

function hasValue(v: unknown): boolean {
  return v !== null && v !== undefined && String(v).trim() !== '';
}

/** Records with each saved value of a default-seeded field handed to DEFAULTRESULT (new objects; the
 *  input records are not changed). Returns the input array itself when the template has no such cell. */
export function feedSavedValuesToDefaultCells(ws: Ws, records: Row[], fieldCells: Record<string, string> | null | undefined): Row[] {
  const fields = fieldCells ? fieldsSeededByDefault(ws, fieldCells) : [];
  if (!fields.length) return records;
  return records.map(rec => {
    for (const f of fields) {
      const k = keyOf(rec, f);
      const v = k === undefined ? undefined : rec[k];
      if (!hasValue(v)) continue;
      return { ...rec, [keyOf(rec, DEFAULT_FIELD) ?? DEFAULT_FIELD]: v };
    }
    return rec;
  });
}
